import { useEffect, useMemo, useState } from "react";
import "./partners.css";

function getImgStyle(p: { width?: number; height?: number }) {
  const style: React.CSSProperties = {
    // default: fill the box while preserving aspect ratio
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
  };
  if (p.width) style.maxWidth = `${p.width}px`; // per-logo width cap
  if (p.height) style.maxHeight = `${p.height}px`; // per-logo height cap
  return style;
}

/** Mobile-only rotator (fades one logo in at a time) */
function MobileLogoRotator({
  items,
  intervalMs = 3000, // 5 minutes
  isMobile = false,
}: {
  items: Array<{ logo: string; alt: string; width?: number }>;
  intervalMs?: number;
  isMobile: boolean;
}) {
  const [index, setIndex] = useState(0);

  // Preload images so the first swap is instant
  useEffect(() => {
    items.forEach((p) => {
      const img = new Image();
      img.src = p.logo;
    });
  }, [items]);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [items.length, intervalMs]);

  return (
    <div className="partner-logo-rotator">
      {items.map((p, i) => (
        <div
          key={p.logo}
          className={`rotator-slide ${i === index ? "active" : ""}`}
          aria-hidden={i === index ? "false" : "true"}
        >
          <img
            src={p.logo}
            alt={p.alt}
            loading="lazy"
            style={isMobile ? getImgStyle(p) : { width: "220px" }}
          />
        </div>
      ))}
    </div>
  );
}

const PartnersSection = () => {
  const partners: Record<
    string,
    Array<{ logo: string; alt: string; width?: number }>
  > = {
    association: [
      {
        logo: "https://res.cloudinary.com/astcdubai/image/upload/v1735883294/dubai_economy_white_vwn1ry.png",
        alt: "Dubai Economy and Tourism",
      },
    ],
    strategic: [
      {
        logo: "https://res.cloudinary.com/astcdubai/image/upload/v1755162004/DIFC_DGT_Brandmark_White_2_th8lcn.png",
        alt: "DIFC",
        width: 230,
      },
    ],
    flying: [
      {
        logo: "https://res.cloudinary.com/astcdubai/image/upload/v1735883694/emirates_white_kyy4oo.png",
        alt: "Emirates",
        width: 450,
      },
    ],
    training: [
      {
        logo: "https://res.cloudinary.com/astcdubai/image/upload/v1735882651/white-logo-astc_wolvoz.png",
        alt: "ASTC",
        width: 250,
      },
      {
        logo: "https://res.cloudinary.com/astcdubai/image/upload/v1757610272/ACI_Logo_k2gqcw.png",
        alt: "ACI",
      },
      {
        logo: "https://res.cloudinary.com/astcdubai/image/upload/v1757440120/ICMA_logo_without_wording_ifaoov.png",
        alt: "ICMA",
      },
    ],
    event: [
      {
        logo: "https://res.cloudinary.com/astcdubai/image/upload/v1761568641/Al_Tayer_Events_Partner_2x_sie1mn.png",
        alt: "Al Tayer Event",
        width: 330,
      },
    ],
  };

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <section id="partners" className="pt-120 pb-120">
      <div className="container">
        {/* Row 1: Association + Strategic */}
        <div className="partners-top-grid">
          <div className="partner-group">
            <h1 className="title text-center">Destination Partner</h1>
            <div className="partner-row">
              {partners.association.map((p) => (
                <div key={p.logo} className="partner association">
                  <div className="partner-logo-box">
                    <img
                      src={p.logo}
                      alt={p.alt}
                      loading="lazy"
                      style={isMobile ? getImgStyle(p) : { width: "120px" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="partner-group">
            <h1 className="title text-center">Strategic Partner</h1>
            <div className="partner-row">
              {partners.strategic.map((p) => (
                <div key={p.logo} className="partner strategic">
                  <div className="partner-logo-box">
                    <img
                      src={p.logo}
                      alt={p.alt}
                      loading="lazy"
                      style={isMobile ? getImgStyle(p) : { width: "220px" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: Flying + Event */}
        <div className="partners-top-grid">
          <div className="partner-group">
            <h1 className="title text-center">Flying Partner</h1>
            <div className="partner-row">
              {partners.flying.map((p) => (
                <div key={p.logo} className="partner flying">
                  <div className="partner-logo-box">
                    <img
                      src={p.logo}
                      alt={p.alt}
                      loading="lazy"
                      style={isMobile ? getImgStyle(p) : { width: "120px" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="partner-group">
            <h1 className="title text-center">Events Management Partner</h1>
            <div className="partner-row">
              {partners.event.map((p) => (
                <div key={p.logo} className="partner event">
                  <div className="partner-logo-box">
                    <img
                      src={p.logo}
                      alt={p.alt}
                      loading="lazy"
                      style={isMobile ? getImgStyle(p) : { width: "175px" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Training */}
        <div className="partner-group pt-80">
          <h1 className="title text-center">Training Partners</h1>

          {/* Desktop/tablet: normal row; Mobile: rotator */}
          {isMobile ? (
            <MobileLogoRotator items={partners.training} isMobile={isMobile} />
          ) : (
            <div className="partner-row one-line">
              {partners.training.map((p) => (
                <div key={p.logo} className="partner training">
                  <div className="partner-logo-box">
                    <img
                      src={p.logo}
                      alt={p.alt}
                      loading="lazy"
                      style={isMobile ? getImgStyle(p) : { width: "200px" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
